/**
 * useEmbedMember · reads corporate member identity from URL params or
 * sessionStorage so pillar pages that render inside the Corporate
 * Dashboard drawer auto-prefill the "Request via Concierge®" form.
 *
 * Contract (accepts BOTH conventions — Corporate Portal + Aurelion):
 *   ?embed=drawer|widget
 *   &tenant=corp:{slug}         (Corporate Portal)
 *   &tenant=aurelion|mindescapes(Aurelion / MindEscapes)
 *   &member_name={First Last}   (Corporate — single string)
 *   &member_first={First}       (Aurelion — split)
 *   &member_last={Last}         (Aurelion — split)
 *   &member_email={email}
 *   &member_phone={+91...}
 *   &member_card={aurelion card no}
 *
 * On first read, values are cached in sessionStorage under
 * `lc_embed_member_v1` so navigation within the iframe still surfaces
 * the same identity.
 *
 * Returns null when no embed context is present. Reads SYNCHRONOUSLY
 * inside a `useState` lazy initializer so any pillar modal doing
 * `useState(embedMember?.name || "")` picks up the prefill on the
 * very first render (no useEffect delay).
 */
import { useState } from "react";

const CACHE_KEY = "lc_embed_member_v1";

function _readSync() {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const p   = url.searchParams;
    const firstName = p.get("member_first") || "";
    const lastName  = p.get("member_last")  || "";
    const combined  = [firstName, lastName].filter(Boolean).join(" ").trim();
    const fresh = {
      embed:   p.get("embed") || null,
      tenant:  p.get("tenant") || null,
      name:    p.get("member_name") || combined || null,
      first:   firstName || null,
      last:    lastName  || null,
      email:   p.get("member_email") || null,
      phone:   p.get("member_phone") || null,
      card:    p.get("member_card")  || null,
    };
    const hasAny =
      fresh.embed || fresh.tenant || fresh.name ||
      fresh.email || fresh.phone  || fresh.card;
    if (hasAny) {
      try {
        window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(fresh));
      } catch (_) { /* private mode · ignore */ }
      return fresh;
    }
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return parsed;
      } catch (_) { /* ignore parse error */ }
    }
  } catch (_) { /* SSR-safe */ }
  return null;
}

export function useEmbedMember() {
  const [ctx] = useState(_readSync);
  return ctx;
}

export default useEmbedMember;
